import { layoutOf, recipeSuggestion, formPages, type NavigationMode } from '@ayumia/component-runtime';
import { useMemo, useState } from 'react';
import { Dialog, DialogActions } from '../../design-system/Overlay';
import { text } from '../../app/i18n';
import type { ProfilePanel } from '../editor/canvas-types';
import { addPage, chooseLayout, declaredPages, placeElement, setNavigation, type Template } from './layout-model';
import { currentLayout, fr, recipesFor, refusalOf, type EditorRegistry, type RegistryLayout } from './layout-registry';
import { NAVIGATION_LABELS } from './form-inspector';
import { Hint } from './inspector-controls';

/**
 * Premier choix de l'auteur (ADR-146 §5.2, maquette « Gabarit et navigation ») : le gabarit de mise en page
 * sur des vignettes, puis le mode de navigation ; la recette, invisible, recommande et refuse avec son motif.
 * Changer de gabarit montre l'effet avant d'appliquer : les éléments sans place équivalente sont listés et
 * gardés dans la zone de dépôt, jamais perdus ; Ctrl Z annule.
 */

const NAV_HELP: Readonly<Record<NavigationMode, string>> = {
  single: 'tout sur un écran qui défile',
  tabs: 'accès libre à chaque partie',
  wizard: 'ordre imposé, Suivant et Précédent, vérification à chaque étape',
  steps_with_summary: 'étapes et synthèse toujours visible',
};

function Thumbnail({ layout, extra }: { layout: RegistryLayout; extra?: ReadonlyMap<string, readonly string[]> | undefined }) {
  const preview = layout.body.preview;
  const contents = (regionId: string): string[] => {
    if (extra !== undefined) return [...(extra.get(regionId) ?? [])];
    return [];
  };
  return (
    <span className="ay-studio-thumb" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${preview.columns}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${preview.rows}, auto)` }}>
      {layout.body.regions.map((region) => {
        const cell = preview.cells[region.region_id];
        if (cell === undefined) return null;
        return (
          <span key={region.region_id} className="ay-studio-thumb__region" style={{ gridColumn: `${cell.column} / span ${cell.column_span ?? 1}`, gridRow: `${cell.row} / span ${cell.row_span ?? 1}` }}>
            <span className="ay-studio-thumb__name">{fr(region.label)}</span>
            {contents(region.region_id).map((label) => <span key={label} className="ay-studio-thumb__block">{label}</span>)}
          </span>
        );
      })}
    </span>
  );
}

/** Contenu des zones après un changement : parties et blocs, par zone. */
function contentsAfter(template: Template, registry: EditorRegistry): Map<string, string[]> {
  const map = new Map<string, string[]>();
  const body = currentLayout(registry, template)?.body;
  const firstSection = body?.regions.find((region) => region.accepts.includes('section'))?.region_id;
  const push = (region: string | undefined, label: string): void => {
    if (region === undefined) return;
    map.set(region, [...(map.get(region) ?? []), label]);
  };
  for (const section of template.sections) push(layoutOf(template)?.placements?.[section.section_id]?.region ?? firstSection, text(section.label, 'fr', 'Partie'));
  for (const block of layoutOf(template)?.blocks ?? []) push(block.region, fr(block.label, 'Bloc'));
  return map;
}

export function LayoutChooser({ open, registry, template, onClose, onApply }: { open: boolean; registry: EditorRegistry; template: Template; onClose: () => void; onApply: (next: Template) => void }) {
  const current = currentLayout(registry, template);
  const [layoutId, setLayoutId] = useState(current?.artifact_id ?? 'core.layout.single_column');
  const [placed, setPlaced] = useState<Record<string, string>>({});
  const [mode, setMode] = useState<NavigationMode | undefined>(layoutOf(template)?.navigation?.mode);
  const [portraitWizard, setPortraitWizard] = useState(layoutOf(template)?.navigation?.mobile === 'wizard');
  const intent = layoutOf(template)?.intent;
  const recommended = useMemo(() => recipesFor(registry, current).map((candidate) => recipeSuggestion(candidate.body, intent)).find((found) => found !== undefined), [registry, current, intent]);
  const changed = useMemo(() => {
    const { template: relocated, report } = chooseLayout(template, registry, layoutId);
    let next = relocated;
    for (const [key, region] of Object.entries(placed)) {
      const [kind, id] = key.split(':') as ['section' | 'block', string];
      next = placeElement(next, { kind, id }, region);
    }
    return { next, report };
  }, [template, registry, layoutId, placed]);
  const target = registry.layouts.find((layout) => layout.artifact_id === layoutId);
  const allowed = (target?.body.navigation.allowed ?? ['single']) as NavigationMode[];
  const chosenMode: NavigationMode = mode !== undefined && allowed.includes(mode) ? mode : (target?.body.navigation.default as NavigationMode | undefined) ?? 'single';
  const withNav = setNavigation(changed.next, { mode: chosenMode, mobile: portraitWizard ? 'wizard' : undefined });
  const refusalFor = (candidate: NavigationMode): string | null => refusalOf(registry, changed.next, setNavigation(changed.next, { mode: candidate }));
  const refused = refusalOf(registry, template, withNav);
  const pending = changed.report.unplaced.filter((entry) => placed[`${entry.kind}:${entry.id}`] === undefined);
  const same = layoutId === current?.artifact_id && layoutOf(template) !== undefined;
  return (
    <Dialog
      title="Gabarit et navigation"
      subtitle={text(template.label, 'fr', 'Formulaire')}
      open={open}
      onClose={onClose}
      size="lg"
      note="Rien n’est supprimé ; Ctrl Z annule."
      footer={<DialogActions cancel={{ label: 'Annuler', onClick: onClose }} primary={{ label: refused !== null ? 'Appliquer · navigation refusée' : pending.length > 0 ? `Appliquer · ${pending.length} à replacer` : 'Appliquer', disabled: refused !== null, onClick: () => onApply(withNav) }} />}
    >
      <section aria-labelledby="chooser-layout" className="ay-studio-chooser">
        <h3 id="chooser-layout" className="ay-props__subtitle">1 · Gabarit de mise en page</h3>
        <div className="ay-studio-chooser__grid" role="radiogroup" aria-label="Gabarit de mise en page">
          {registry.layouts.map((layout) => (
            <button key={layout.artifact_id} type="button" role="radio" aria-checked={layout.artifact_id === layoutId} className="ay-studio-chooser__card" onClick={() => { setLayoutId(layout.artifact_id); setPlaced({}); }}>
              <Thumbnail layout={layout} extra={layout.artifact_id === layoutId ? contentsAfter(changed.next, registry) : undefined} />
              <strong>{fr(layout.body.label)}</strong>
              {recommended?.layout_ref === layout.artifact_id && <span className="ay-studio__layer">Recommandé pour ce formulaire</span>}
              <span className="ay-studio__muted">{fr(layout.body.description)}</span>
            </button>
          ))}
        </div>
      </section>
      <section aria-labelledby="chooser-nav" className="ay-studio-chooser">
        <h3 id="chooser-nav" className="ay-props__subtitle">2 · Navigation entre les pages</h3>
        <p className="ay-studio__muted">{formPages(changed.next).length} page(s) : {formPages(changed.next).map((page) => fr(page.label, 'Page')).join(', ')}.</p>
        <div role="radiogroup" aria-label="Mode de navigation" className="ay-studio-chooser__navs">
          {allowed.map((candidate) => {
            const why = refusalFor(candidate);
            return (
              <button key={candidate} type="button" role="radio" aria-checked={candidate === chosenMode} aria-describedby={why === null ? undefined : `nav-why-${candidate}`} className={why === null ? '' : 'is-muted'} onClick={() => setMode(candidate)}>
                <strong>{NAVIGATION_LABELS[candidate]}</strong>
                <span className="ay-studio__muted">{NAV_HELP[candidate]}</span>
                {why !== null && <span id={`nav-why-${candidate}`} className="ay-studio__muted">Refusé : {why}</span>}
              </button>
            );
          })}
        </div>
        {refused !== null && <p className="ay-studio__refusal" role="alert">{refused}</p>}
        <label className="ay-props__row ay-props__row--inline">
          <input type="checkbox" checked={portraitWizard} onChange={(event) => setPortraitWizard(event.target.checked)} />
          <span>Sur téléphone : passer en Assistant (une page par écran)</span>
        </label>
      </section>
      <section aria-labelledby="chooser-effect" className="ay-studio-chooser">
        <h3 id="chooser-effect" className="ay-props__subtitle">3 · Effet du changement</h3>
        {same ? (
          <p className="ay-studio__muted">Gabarit inchangé : rien ne bouge.</p>
        ) : changed.report.unplaced.length === 0 ? (
          <p className="ay-studio__muted">Tout trouve sa place : {changed.report.moved.length} élément(s) suivent leur zone.</p>
        ) : (
          <div className="ay-studio-chooser__orphans">
            <strong>{pending.length === 0 ? 'Tous les éléments ont une place.' : `${pending.length} élément(s) sans place équivalente — gardés dans « ${fr(target?.body.regions.find((region) => region.region_id === target.body.drop_region)?.label, 'la zone de dépôt')} »`}</strong>
            {changed.report.unplaced.map((entry) => (
              <div key={`${entry.kind}:${entry.id}`} className="ay-studio-chooser__orphan">
                <span>{entry.label}</span>
                <span className="ay-studio__muted">était dans {fr(current?.body.regions.find((region) => region.region_id === entry.from)?.label, 'une zone')}</span>
                {(target?.body.regions ?? []).filter((region) => region.accepts.includes(entry.kind === 'section' ? 'section' : 'display') && region.role !== 'footer').map((region) => (
                  <button key={region.region_id} type="button" className="ay-button ay-button--secondary" aria-pressed={placed[`${entry.kind}:${entry.id}`] === region.region_id} onClick={() => setPlaced((value) => ({ ...value, [`${entry.kind}:${entry.id}`]: region.region_id }))}>Placer dans {fr(region.label)}</button>
                ))}
              </div>
            ))}
          </div>
        )}
      </section>
    </Dialog>
  );
}

/** Panneau « Étapes » : la navigation et les pages du formulaire, l'accès au choix du gabarit. */
export function stepsPanel(registry: EditorRegistry, onChooseLayout: (document: Template, apply: (next: Template) => void) => void): ProfilePanel<Template> {
  return {
    id: 'steps',
    label: 'Pages',
    glyph: '▤',
    render: ({ document, apply, selected, select }) => {
      const pages = formPages(document);
      const declared = declaredPages(document);
      const layout = currentLayout(registry, document);
      const last = declared[declared.length - 1];
      const splitFrom = declared.length === 0 ? null : last !== undefined && last.sections.length > 1 ? last : null;
      return (
        <div className="ay-studio-steps-panel">
          <button type="button" className="ay-studio-steps-panel__nav" onClick={() => onChooseLayout(document, apply)}>
            <span className="ay-studio__muted">Navigation · gabarit</span>
            <strong>{layoutOf(document) === undefined ? 'Aucun gabarit' : `${NAVIGATION_LABELS[layoutOf(document)?.navigation?.mode ?? layout?.body.navigation.default ?? 'single']} · ${fr(layout?.body.label)}`}</strong>
            <span>Changer ›</span>
          </button>
          <ol className="ay-studio-steps-panel__pages" aria-label="Pages">
            {pages.map((page, index) => (
              <li key={page.page_id}>
                <button type="button" aria-current={selected === `page:${page.page_id}` ? 'true' : undefined} disabled={declared.length === 0} onClick={() => select(`page:${page.page_id}`)}>
                  <strong>{index + 1} · {fr(page.label, 'Page')}</strong>
                  <span className="ay-studio__muted">{page.sections.length} partie(s)</span>
                </button>
              </li>
            ))}
          </ol>
          {declared.length === 0 ? (
            <button type="button" className="ay-button ay-button--secondary" onClick={() => apply(addPage(document, { fr: 'Page 1' }, document.sections.map((section) => section.section_id)).template)}>Découper en pages</button>
          ) : (
            <button type="button" className="ay-button ay-button--secondary" disabled={splitFrom === null} onClick={() => splitFrom !== null && apply(addPage(document, { fr: `Page ${declared.length + 1}` }, [splitFrom.sections[splitFrom.sections.length - 1]!]).template)}>
              Ajouter une page
            </button>
          )}
          {declared.length > 0 && splitFrom === null && <Hint label="Pourquoi « Ajouter une page » est grisé ?">Chaque page montre au moins une partie : la nouvelle page reprend la dernière partie de la page précédente.</Hint>}
        </div>
      );
    },
  };
}
